import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

import EditIcon from "@mui/icons-material/Edit";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { useSelector, useDispatch } from "react-redux";
import { selectRefreshToken } from "../../redux/refreshSlice";
import API_ENDPOINTS from "../../apis/endpoints";
import "./LoginContent.css";

// Import your images
import backgroundImage from "../../assets/images/bg1.jpg";
import loginBackgroundImage from "../../assets/images/login-background.png";
import loginWelcome from "../../assets/images/collectiveartists.png";
import ButtonComponent from "../../components/ButtonComponent";

const CORPORATE_LOGIN_ATTEMPTS = 3;
const CORPORATE_VERIFICATION_ATTEMPTS = 10;

function LoginContent() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector(selectRefreshToken);

    const [loading, setLoading] = useState(false);
    const [isPhoneLogin, setIsPhoneLogin] = useState(false);
    const [deviceId, setDeviceId] = useState("");
    const [countryCode, setCountryCode] = useState("+91");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState(Array(6).fill(""));
    const [verificationToken, setVerificationToken] = useState("");
    const [attemptsLeft, setAttemptsLeft] = useState(CORPORATE_LOGIN_ATTEMPTS);
    const [verificationAttemptsLeft, setVerificationAttemptsLeft] = useState(
        CORPORATE_VERIFICATION_ATTEMPTS
    );
    const [allowResend, setAllowResend] = useState(true);
    const [error, setError] = useState("");
    const [confirmNumber, setConfirmNumber] = useState("");

    useEffect(() => {
        createDeviceId();
    }, []);

    const createDeviceId = async () => {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const visitorId = result.visitorId;
        const fingerprint = JSON.stringify(result);

        let formData = new FormData();
        formData.append("visitor_id", visitorId);
        formData.append("fingerprint", fingerprint);

        try {
            const response = await fetch(`${API_ENDPOINTS.CREATE_DEVICE_ID}`, {
                method: "POST",
                // headers: { 'Content-Type': 'application/json' },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setDeviceId(data.data.device_id);
                localStorage.setItem("device-id", data.data.device_id);
            }
        } catch (error) {
            console.error("Error creating device ID:", error);
        }
    };

    const handleSendVerificationCode = async () => {
        try {
            let formData = new FormData();
            formData.append("phone_number", phoneNumber);
            formData.append("country_code", countryCode);
            formData.append("type", "phone-send");

            const response = await fetch(`${API_ENDPOINTS.LOGIN}`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    "x-cc-device-id": deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setVerificationToken(data.data.token);
                setAttemptsLeft(data.data.attempts_left);
                setConfirmNumber(`${countryCode}${phoneNumber}`);
                // You might want to start a timer here based on data.data.expires
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error sending verification code:", error);
            setError("Error sending verification code");
        }
    };

    const handleVerifyCode = async () => {
        const code = otp.join("");

        try {
            let formData = new FormData();
            formData.append("token", verificationToken);
            formData.append("code", code);
            formData.append("type", "phone-verify");

            const response = await fetch(`${API_ENDPOINTS.LOGIN}`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    "x-cc-device-id": deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                // Handle successful verification
                window.location.href = "/dashboard";
            } else {
                setVerificationAttemptsLeft(data.attempts_left);
                setError("Invalid code");
            }
        } catch (error) {
            console.error("Error verifying code:", error);
            setError("Error verifying code");
        }
    };

    const handleResendCode = async () => {
        try {
            let formData = new FormData();
            formData.append("token", verificationToken);
            formData.append("type", "phone-resend");

            const response = await fetch(`${API_ENDPOINTS.LOGIN}`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    "x-cc-device-id": deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setVerificationToken(data.data.token);
                setAttemptsLeft(data.data.attempts_left);
                setAllowResend(data.data.allow_resend);
                // You might want to start a timer here based on data.data.expires
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error resending code:", error);
            setError("Error resending code");
        }
    };

    const handleOtpChange = (index, value) => {
        if (/^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < otp.length - 1) {
                document.getElementById(`otp-input-${index + 1}`).focus();
            }
        }
    };

    const toggleLoginMethod = () => {
        setIsPhoneLogin(!isPhoneLogin);
        setOtp(Array(6).fill(""));
        setPhoneNumber("");
        setConfirmNumber("");
        setError("");
    };
    const handleLoginSuccess = async (response) => {
        const idToken = response.credential;

        let formData = new FormData();
        formData.append("token", idToken);
        formData.append("type", "google");
        try {
            const response = await fetch(`${API_ENDPOINTS.LOGIN}`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    "x-cc-device-id": deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                localStorage.setItem("refresh_token", data.data.refresh_token);

                setVerificationToken(data.data.token);
                setAttemptsLeft(data.data.attempts_left);
                setAllowResend(data.data.allow_resend);
                window.location.href = "/dashboard";
                // Adjust as needed

                // You might want to start a timer here based on data.data.expires
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error resending code:", error);
            setError("Error resending code");
        }
    };

    return (
        <div
            className="login-page"
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundAttachment: "fixed",
            }}
        >
            <div
                className="login-content"
                style={{
                    backgroundImage: `url(${loginBackgroundImage})`,
                }}
            >
                <div className="login-left">
                    <img
                        src={loginWelcome}
                        alt="loginWelcome"
                        className="welcome-image"
                    />
                </div>

                <div className="login-right">
                    {/* <img src={bigCollective} alt="under25-logo" className="bigCollective-logo" /> */}
                    <h1>{isPhoneLogin ? "Enter OTP" : "Sign In"}</h1>
                    <p>
                        {!isPhoneLogin &&
                            "Sign in with your Google account to get started"}
                    </p>

                    {!isPhoneLogin ? (
                        <div className="login-buttons">
                            {/* <GoogleButton onClick={handleGoogleLogin} disabled={loading} /> */}
                            <GoogleOAuthProvider
                                clientId={process.env.REACT_APP_CLIENT_ID}
                            >
                                <GoogleLogin
                                    onSuccess={handleLoginSuccess}
                                    onError={() => {
                                        console.log("Login Failed");
                                    }}
                                    auto_select
                                    ux_mode="popup"
                                    useOneTap
                                    // hosted_domain="collectiveartists.com"
                                    use_fedcm_for_prompt
                                    width={320}
                                />
                            </GoogleOAuthProvider>
                            {/* <button
                                className="login-button"
                                onClick={toggleLoginMethod}
                            >
                                {" "}
                                <PhoneIcon /> Phone Number{" "}
                            </button> */}
                        </div>
                    ) : (
                        <div className="otp-inputs">
                            {!confirmNumber ? (
                                <div className="phone-inputs">
                                    <div className="phone-input-container">
                                        <select
                                            className="country-code"
                                            value={countryCode}
                                            onChange={(e) =>
                                                setCountryCode(e.target.value)
                                            }
                                        >
                                            <option value="+91">
                                                +91 (India)
                                            </option>
                                            <option value="+1">+1 (USA)</option>
                                            <option value="+44">
                                                +44 (UK)
                                            </option>
                                        </select>
                                        <input
                                            type="text"
                                            placeholder="Enter your phone number"
                                            value={phoneNumber}
                                            onChange={(e) =>
                                                setPhoneNumber(e.target.value)
                                            }
                                            className="phone-number-input"
                                            maxLength={10}
                                            minLength={10}
                                        />
                                    </div>
                                    {/* <button className="resend-otp" onClick={handleSendVerificationCode}></button> */}

                                    <ButtonComponent
                                        size="small"
                                        color="primary"
                                        name="Next"
                                        onClick={handleSendVerificationCode}
                                    />
                                </div>
                            ) : (
                                <div className="confirm-number">
                                    <span>{confirmNumber}</span>
                                    <EditIcon
                                        onClick={() => setConfirmNumber("")}
                                        className="edit-icon"
                                    />
                                </div>
                            )}

                            {confirmNumber && (
                                <div className="otp-boxes-container">
                                    <div>
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`otp-input-${index}`}
                                                type="text"
                                                maxLength="1"
                                                value={digit}
                                                onChange={(e) =>
                                                    handleOtpChange(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                                className="otp-boxes"
                                            />
                                        ))}
                                    </div>
                                    {/* <button className="verify-button" onClick={handleVerifyCode}></button> */}
                                    <ButtonComponent
                                        size="large"
                                        color="primary"
                                        name="Verify OTP"
                                        onClick={handleVerifyCode}
                                    />

                                    <button
                                        className="resend-otp"
                                        onClick={handleResendCode}
                                        disabled={!allowResend}
                                    >
                                        Resend Code
                                    </button>
                                </div>
                            )}

                            {/* <p onClick={toggleLoginMethod} className="toggle-login">or Login with Google</p> */}
                            <br />
                            {/* <GoogleButton onClick={handleGoogleLogin} disabled={loading} /> */}

                            <GoogleOAuthProvider
                                clientId={process.env.REACT_APP_CLIENT_ID}
                            >
                                <GoogleLogin
                                    onSuccess={handleLoginSuccess}
                                    onError={() => {
                                        console.log("Login Failed");
                                    }}
                                    auto_select
                                    ux_mode="popup"
                                    useOneTap
                                    // hosted_domain="collectiveartists.com"
                                    use_fedcm_for_prompt
                                    style={{ width: "600px" }}
                                />
                            </GoogleOAuthProvider>
                        </div>
                    )}

                    {error && <p className="error">{error}</p>}
                    {attemptsLeft < CORPORATE_LOGIN_ATTEMPTS && (
                        <p>Attempts left: {attemptsLeft}</p>
                    )}
                    {verificationAttemptsLeft <
                        CORPORATE_VERIFICATION_ATTEMPTS && (
                        <p>
                            Verification attempts left:{" "}
                            {verificationAttemptsLeft}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LoginContent;
